import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AtSign, AlertTriangle, CheckCircle2, X, Loader2, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import ModalPortal from '../ModalPortal';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ChangeEmailModal — super-admin-only tool to replace a user's account/login
 * email. Preserves user_id, password, role, leadership position, badges,
 * community history, memberships and email preferences — only the login
 * identifier changes.
 *
 * Historical email records (sent campaigns, delivery events) intentionally
 * keep the address they were actually sent to.
 */
const ChangeEmailModal = ({ user, onClose, onSaved }) => {
  const [newEmail, setNewEmail] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  useBodyScrollLock(!!user);

  useEffect(() => {
    setNewEmail('');
    setConfirm(false);
  }, [user?.id]);

  if (!user) return null;

  const normalized = newEmail.trim().toLowerCase();
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const sameAsCurrent = normalized === (user.email || '').toLowerCase();
  const validShape = EMAIL_RE.test(normalized);
  const canSubmit = validShape && !sameAsCurrent && confirm && !saving;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/users/${user.id}/email`,
        { new_email: normalized },
        { withCredentials: true },
      );
      toast.success(`Email updated for ${data.name || user.name}`);
      onSaved?.(data);
      onClose();
    } catch (err) {
      const code = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (code === 409) {
        toast.error(detail || 'That email already belongs to another account.');
      } else if (code === 400) {
        toast.error(detail || 'Please provide a valid email address.');
      } else if (code === 403) {
        toast.error('Only Super Admin can change another user\'s email.');
      } else {
        toast.error('Could not update email — please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalPortal>
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="change-email-modal"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-gradient-to-r from-[#002249] to-[#0a3570] px-6 py-5 rounded-t-2xl text-white flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider opacity-80">
                <ShieldCheck className="w-3.5 h-3.5" /> Super Admin
              </div>
              <h3 className="font-bold text-lg mt-1">Change Account Email</h3>
            </div>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white"
              data-testid="change-email-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            {/* Target user context */}
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <div className="font-semibold text-[#1a1a1a]">{user.name}</div>
              <div className="text-slate-500 text-xs mt-0.5">
                {user.role?.replace(/_/g, ' ')} · ID {user.id?.slice(0, 8)}…
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wide">Current Account Email</Label>
              <div
                className="mt-1 px-3 py-2 bg-slate-100 border border-slate-200 rounded-md text-sm font-mono text-slate-700 break-all"
                data-testid="current-email"
              >
                {user.email}
              </div>
            </div>

            <div>
              <Label htmlFor="new-email" className="text-xs text-slate-500 uppercase tracking-wide">
                New Account Email
              </Label>
              <div className="mt-1 relative">
                <AtSign className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  id="new-email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="e.g. president@dominicanchamberfl.org"
                  className="pl-9"
                  data-testid="new-email-input"
                />
              </div>
              {newEmail && !validShape && (
                <p className="text-xs text-rose-600 mt-1">Please enter a valid email address.</p>
              )}
              {sameAsCurrent && (
                <p className="text-xs text-amber-700 mt-1">That's the same email as the current one.</p>
              )}
            </div>

            {/* What stays, what changes */}
            <div className="rounded-lg border border-slate-200 bg-white text-xs">
              <div className="px-3 py-2 border-b border-slate-100 text-slate-500 uppercase font-semibold tracking-wide">
                What this does
              </div>
              <ul className="divide-y divide-slate-100">
                <li className="flex items-start gap-2 px-3 py-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">New email becomes the login identifier immediately.</span>
                </li>
                <li className="flex items-start gap-2 px-3 py-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Password, role, leadership position, badges and permissions stay unchanged.</span>
                </li>
                <li className="flex items-start gap-2 px-3 py-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Community posts, comments, committees, memberships and email preferences stay connected.</span>
                </li>
                <li className="flex items-start gap-2 px-3 py-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Historical emails already sent remain on their original address — this is intentional.</span>
                </li>
              </ul>
            </div>

            {/* Confirmation checkbox */}
            <label className="flex items-start gap-2 cursor-pointer text-sm text-slate-700">
              <input
                type="checkbox"
                checked={confirm}
                onChange={(e) => setConfirm(e.target.checked)}
                className="mt-1 w-4 h-4 accent-[#002249]"
                data-testid="change-email-confirm"
              />
              <span>I understand this changes the account's login email and will be recorded in the audit log.</span>
            </label>

            <div className="flex items-center justify-end gap-2 pt-1">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={saving}
                data-testid="change-email-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={!canSubmit}
                className="bg-[#002249] hover:bg-[#0a3570]"
                data-testid="change-email-submit"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save New Email'}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </ModalPortal>
  );
};

export default ChangeEmailModal;
